// ============================================
// ExerciseEngine - Motor de Ejercicios Pedagógicos
// Diseñado para estudiantes de 5to básico (~10-11 años)
// ============================================

import {
  type NoteName,
  type NeckPosition,
  noteToSemitones,
  semitonesToNote,
  getNoteAtPosition,
  buildScalePattern,
  buildScaleHandFriendlySequence,
  OPEN_STRINGS,
  shouldUseFlats,
} from "./guitarEngine";

import {
  CHORD_CATALOG,
  getIntervalSemitones,
  getIntervalDegrees,
  type ExtendedChordQuality,
} from "./chordEngine";

import {
  getAllPositions,
  buildRoute,
  findBestAnchor,
  noteNameToPitchClass,
  pitchClassToSpanishName,
  type NavCoord,
} from "./noteNavigator";

// ─── Tipos ───

export type ExerciseCategory = "notas" | "acordes" | "escalas" | "tecnica";
export type ExerciseDifficulty = "facil" | "medio" | "dificil";
export type ExerciseType = "guided" | "quiz" | "click" | "sequence" | "practice";

export interface ExerciseDef {
  id: string;
  title: string;
  category: ExerciseCategory;
  difficulty: ExerciseDifficulty;
  type: ExerciseType;
  description: string; // lenguaje de niño
  emoji: string;
  bpm: number;
  generate: () => ExerciseState;
  checkAnswer?: (state: ExerciseState, answer: unknown) => CheckResult;
}

export interface ExerciseState {
  exerciseId: string;
  title: string;
  instructions: string[]; // paso a paso, frases cortas
  neckPositions: NeckPosition[]; // posiciones a mostrar
  sequence?: NeckPosition[]; // secuencia para animación
  activeNote?: { string: number; fret: number } | null;
  quizOptions?: string[]; // opciones para quiz
  correctAnswer?: unknown; // respuesta correcta
  targetNote?: string; // para ejercicios de "encuentra X"
  targetPositions?: { string: number; fret: number }[]; // posiciones válidas para click
  stars?: number; // recompensa visual
  tips?: string[]; // consejos pedagógicos
  progressTotal?: number; // pasos totales
}

export interface CheckResult {
  correct: boolean;
  message: string; // feedback para el niño
  emoji: string;
  stars: number; // 0-3
}

// ─── Helpers ───

const NOTAS_POOL = ["C", "D", "E", "F", "G", "A", "B"] as NoteName[];
const NOTAS_CON_ALTERACIONES: NoteName[] = [
  "C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B",
];

function rand<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function noteToSpanish(note: NoteName): string {
  const map: Record<string, string> = {
    C: "Do", "C#": "Do#", Db: "Reb",
    D: "Re", "D#": "Re#", Eb: "Mib",
    E: "Mi", F: "Fa", "F#": "Fa#", Gb: "Solb",
    G: "Sol", "G#": "Sol#", Ab: "Lab",
    A: "La", "A#": "La#", Bb: "Sib",
    B: "Si",
  };
  return map[note] || note;
}

function getDegreeName(degree: string): string {
  const map: Record<string, string> = {
    R: "Raíz", "3": "Tercera", "b3": "Tercera menor",
    "5": "Quinta", "b5": "Quinta disminuida", "#5": "Quinta aumentada",
    "7": "Séptima mayor", "b7": "Séptima menor",
    "9": "Novena", "11": "Onceava", "13": "Treceava",
    "2": "Segunda", "4": "Cuarta", "6": "Sexta",
  };
  return map[degree] || degree;
}

// ─── Generadores de Ejercicios ───

// ============================================
// 1. NOTAS EN EL MÁSTIL
// ============================================

/** EJERCICIO: "¿Qué nota es esta?" — El sistema marca una nota, el niño debe identificarla. */
function genWhatNoteIsThis(preferredNote?: NoteName): ExerciseState {
  const note =
    preferredNote && NOTAS_POOL.includes(preferredNote) ? preferredNote : rand(NOTAS_POOL);
  const all = getAllPositions(note, 15);
  const target = rand(all);

  // Generar 3 opciones incorrectas
  const wrongPool = NOTAS_POOL.filter((n) => n !== note);
  const options = shuffle([note, ...shuffle(wrongPool).slice(0, 3)]);

  return {
    exerciseId: "what-note",
    title: "¿Qué nota es esta?",
    instructions: [
      "Mira la nota resaltada en el mástil.",
      "¿Recuerdas cómo se llama?",
      "Elige la respuesta correcta.",
    ],
    neckPositions: [
      {
        string: target.string,
        fret: target.fret,
        note: target.note as NoteName,
        highlight: "active",
      },
    ],
    quizOptions: options.map((n) => noteToSpanish(n)),
    correctAnswer: noteToSpanish(note),
    stars: 3,
    tips: ["Recuerda los nombres: Do, Re, Mi, Fa, Sol, La, Si"],
    progressTotal: 1,
  };
}

/** EJERCICIO: "Encuentra el ___" — El sistema pide una nota, el niño debe hacer click en el mástil. */
function genFindTheNote(preferredNote?: NoteName): ExerciseState {
  const note =
    preferredNote && NOTAS_POOL.includes(preferredNote) ? preferredNote : rand(NOTAS_POOL);
  const all = getAllPositions(note, 15);

  return {
    exerciseId: "find-note",
    title: `Encuentra el ${noteToSpanish(note)}`,
    instructions: [
      `Tu misión: tocar un "${noteToSpanish(note)}" en el mástil.`,
      "Hay varios escondidos. ¡Busca bien!",
      "Haz click en cualquiera de ellos.",
    ],
    neckPositions: all.map((p) => ({
      string: p.string,
      fret: p.fret,
      note: p.note as NoteName,
      highlight: "scale" as const,
    })),
    targetNote: noteToSpanish(note),
    targetPositions: all.map((p) => ({ string: p.string, fret: p.fret })),
    stars: 3,
    tips: ["Pista: cada cuerda tiene un nombre. La cuerda 6 es Mi, la 5 es La..."],
    progressTotal: 1,
  };
}

/** EJERCICIO: "Sigue el Dibujito" — Ruta mnemotécnica animada. */
function genFollowTheDrawing(): ExerciseState {
  const note = rand(NOTAS_POOL);
  const route = findBestAnchor(note, 15);
  if (!route) return genFindTheNote();

  const fullRoute = buildRoute(route.string, route.fret, 15);
  if (!fullRoute) return genFindTheNote();

  const all = getAllPositions(note, 15);
  const seq: NeckPosition[] = fullRoute.allCoords.map((c, i) => ({
    string: c.string,
    fret: c.fret,
    note: c.note as NoteName,
    highlight: i === 0 ? "root" : "scale",
  }));

  return {
    exerciseId: "follow-drawing",
    title: `Sigue el dibujito del ${noteToSpanish(note)}`,
    instructions: [
      "Tu profe tiene un truco para encontrar la misma nota en todas las cuerdas.",
      "Mira cómo saltamos de cuerda en cuerda.",
      "¡Fíjate en los números de los trastes!",
    ],
    neckPositions: all.map((p) => ({
      string: p.string,
      fret: p.fret,
      note: p.note as NoteName,
      highlight: "scale" as const,
    })),
    sequence: seq,
    activeNote: { string: seq[0].string, fret: seq[0].fret },
    stars: 3,
    tips: [
      "Cada salto tiene un nombre: 'salto 1 espacio', 'salto 2', 'paralelismo'...",
      "Desde la cuerda 2 (Si) siempre bajamos a la cuerda 5 (La).",
    ],
    progressTotal: seq.length,
  };
}

/** EJERCICIO: "Toca todas las ___ de grave a agudo" — Secuencia ordenada. */
function genPlayAllGraveToAgudo(): ExerciseState {
  const note = rand(NOTAS_POOL);
  const all = getAllPositions(note, 15);
  // Ordenar de grave (cuerda 6) a agudo (cuerda 1), y dentro de cada cuerda de traste bajo a alto
  const sorted = [...all].sort((a, b) => {
    if (b.string !== a.string) return b.string - a.string;
    return a.fret - b.fret;
  });

  const seq: NeckPosition[] = sorted.map((p) => ({
    string: p.string,
    fret: p.fret,
    note: p.note as NoteName,
    highlight: "scale",
  }));

  return {
    exerciseId: "grave-to-agudo",
    title: `Toca todas las ${noteToSpanish(note)}`,
    instructions: [
      `Encuentra todas las ${noteToSpanish(note)} del mástil.`,
      "Empieza por la cuerda más gruesa (la de arriba).",
      "Baja cuerda por cuerda hasta la más delgada.",
    ],
    neckPositions: seq.map((p) => ({ ...p, highlight: "scale" })),
    sequence: seq,
    activeNote: { string: seq[0].string, fret: seq[0].fret },
    stars: 3,
    tips: ["La cuerda 6 es la más gruesa (Mi grave). La cuerda 1 es la más delgada (Mi agudo)."],
    progressTotal: seq.length,
  };
}

// ============================================
// 2. ACORDES
// ============================================

/** EJERCICIO: "Las 3 notas del acorde" — Identifica las notas de un acorde Mayor. */
function genChordNotesQuiz(): ExerciseState {
  const root = rand(NOTAS_POOL);
  const useFlats = shouldUseFlats(root, "major");
  const rootSemi = noteToSemitones(root);
  const intervals = [0, 4, 7]; // Mayor
  const notes = intervals.map((i) => semitonesToNote(rootSemi + i, useFlats));

  // Encontrar una posición del acorde en el mástil (simple: 3 cuerdas consecutivas)
  const positions: NeckPosition[] = [];
  let startString = rand([4, 5, 6]);
  for (let i = 0; i < 3; i++) {
    const s = startString - i;
    if (s < 1) break;
    const targetSemi = (rootSemi + intervals[i]) % 12;
    for (let f = 0; f <= 12; f++) {
      const pos = getNoteAtPosition(s, f);
      if (noteToSemitones(pos.note) === targetSemi) {
        positions.push({
          string: s,
          fret: f,
          note: pos.note,
          degree: ["R", "3", "5"][i] as any,
          highlight: i === 0 ? "root" : i === 1 ? "third" : "fifth",
          finger: i + 1,
        });
        break;
      }
    }
  }

  // Opciones: una correcta + 3 incorrectas
  const correct = notes.map((n) => noteToSpanish(n)).join(" - ");
  const wrong1 = [root, rand(NOTAS_POOL), rand(NOTAS_POOL)].map(noteToSpanish).join(" - ");
  const wrong2 = [root, noteToSpanish(semitonesToNote(rootSemi + 3, useFlats) as NoteName), noteToSpanish(semitonesToNote(rootSemi + 7, useFlats) as NoteName)].join(" - ");
  const wrong3 = [noteToSpanish(root), "Re", "Fa"].join(" - ");
  const options = shuffle([correct, wrong1, wrong2, wrong3]);

  return {
    exerciseId: "chord-notes",
    title: `¿Qué notas tiene ${noteToSpanish(root)} Mayor?`,
    instructions: [
      "Mira el acorde dibujado en el mástil.",
      "Cada punto es una nota diferente.",
      "¿Puedes nombrar las 3 notas?",
    ],
    neckPositions: positions,
    quizOptions: options,
    correctAnswer: correct,
    stars: 3,
    tips: ["Un acorde Mayor siempre tiene: Raíz + Tercera + Quinta."],
    progressTotal: 1,
  };
}

/** EJERCICIO: "Encuentra el bajo" — Identifica la nota más grave de un acorde en inversión. */
function genFindTheBass(): ExerciseState {
  const root = rand(NOTAS_POOL);
  const useFlats = shouldUseFlats(root, "major");
  const rootSemi = noteToSemitones(root);
  const inversions: { name: string; intervals: number[]; degrees: string[]; bassName: string }[] = [
    { name: "fundamental", intervals: [0, 4, 7], degrees: ["R", "3", "5"], bassName: noteToSpanish(root) },
    { name: "1ª inversión", intervals: [4, 7, 12], degrees: ["3", "5", "R"], bassName: noteToSpanish(semitonesToNote(rootSemi + 4, useFlats) as NoteName) },
    { name: "2ª inversión", intervals: [7, 12, 16], degrees: ["5", "R", "3"], bassName: noteToSpanish(semitonesToNote(rootSemi + 7, useFlats) as NoteName) },
  ];
  const inv = rand(inversions);

  // Construir posición simple
  const positions: NeckPosition[] = [];
  let startString = rand([4, 5, 6]);
  for (let i = 0; i < 3; i++) {
    const s = startString - i;
    if (s < 1) break;
    const targetSemi = (rootSemi + inv.intervals[i]) % 12;
    for (let f = 0; f <= 12; f++) {
      const pos = getNoteAtPosition(s, f);
      if (noteToSemitones(pos.note) === targetSemi) {
        positions.push({
          string: s,
          fret: f,
          note: pos.note,
          degree: inv.degrees[i] as any,
          highlight: i === 0 ? "root" : i === 1 ? "third" : "fifth",
          finger: i + 1,
        });
        break;
      }
    }
  }

  const options = shuffle([inv.bassName, noteToSpanish(root), noteToSpanish(semitonesToNote(rootSemi + 4, useFlats) as NoteName), noteToSpanish(semitonesToNote(rootSemi + 7, useFlats) as NoteName)]);

  return {
    exerciseId: "find-bass",
    title: `¿Qué nota está en el bajo? (${inv.name})`,
    instructions: [
      "La 'nota bajo' es la nota más grave del acorde.",
      "Es la que suena en la cuerda de más arriba.",
      "¿Cómo se llama esa nota?",
    ],
    neckPositions: positions,
    quizOptions: options,
    correctAnswer: inv.bassName,
    stars: 3,
    tips: [
      "En posición fundamental, el bajo es la Raíz.",
      "En 1ª inversión, el bajo es la Tercera.",
      "En 2ª inversión, el bajo es la Quinta.",
    ],
    progressTotal: 1,
  };
}

/** EJERCICIO: "Construye el acorde ___" — Click en las 3 notas del acorde. */
function genBuildTheChord(): ExerciseState {
  const root = rand(NOTAS_POOL);
  const useFlats = shouldUseFlats(root, "major");
  const rootSemi = noteToSemitones(root);
  const notes = [0, 4, 7].map((i) => semitonesToNote(rootSemi + i, useFlats));
  const noteNames = notes.map((n) => noteToSpanish(n));

  // Todas las posiciones de las 3 notas
  const allTargetPositions: { string: number; fret: number }[] = [];
  const neckPositions: NeckPosition[] = [];

  for (const note of notes) {
    const positions = getAllPositions(note, 15);
    allTargetPositions.push(...positions.map((p) => ({ string: p.string, fret: p.fret })));
    neckPositions.push(
      ...positions.map((p) => ({
        string: p.string,
        fret: p.fret,
        note: p.note as NoteName,
        highlight: "scale" as const,
      }))
    );
  }

  return {
    exerciseId: "build-chord",
    title: `Construye ${noteToSpanish(root)} Mayor`,
    instructions: [
      `Necesitas encontrar 3 notas: ${noteNames.join(", ")}.`,
      "Toca una nota de cada tipo en el mástil.",
      "Pueden estar en cualquier cuerda y traste.",
    ],
    neckPositions,
    targetNote: noteNames.join(", "),
    targetPositions: allTargetPositions,
    correctAnswer: noteNames,
    stars: 3,
    tips: [
      "Pista 1: busca la Raíz (" + noteNames[0] + ").",
      "Pista 2: busca la Tercera (" + noteNames[1] + ").",
      "Pista 3: busca la Quinta (" + noteNames[2] + ").",
    ],
    progressTotal: 3,
  };
}

// ============================================
// 3. ESCALAS
// ============================================

/** EJERCICIO: "Sube la escala de ___" — Secuencia animada de escala mayor. */
function genScaleClimb(preferredRoot?: NoteName): ExerciseState {
  const root =
    preferredRoot && NOTAS_POOL.includes(preferredRoot) ? preferredRoot : rand(NOTAS_POOL);
  const scale = buildScalePattern(root, "major", 15);

  // Construir secuencia mano-amigable (una sola octava, subiendo)
  const seq = buildScaleHandFriendlySequence(root, "major", {
    octaves: 1,
    direction: "asc",
    maxFretSpan: 4,
    startString: 6,
    startFret: preferredRoot ? 0 : Math.floor(Math.random() * 3),
  });

  return {
    exerciseId: "scale-climb",
    title: `Sube la escala de ${noteToSpanish(root)} Mayor`,
    instructions: [
      "Una escala es como una escalera de notas.",
      "Toca cada nota en orden, de grave a agudo.",
      "Sigue los números de los dedos.",
    ],
    neckPositions: scale.positions,
    sequence: seq,
    activeNote: seq.length > 0 ? { string: seq[0].string, fret: seq[0].fret } : null,
    stars: 3,
    tips: [
      "La escala Mayor tiene 7 notas: Do Re Mi Fa Sol La Si (en el caso de Do).",
      "Fíjate en los espacios: algunos saltos son de 1 traste, otros de 2.",
    ],
    progressTotal: seq.length,
  };
}

/** EJERCICIO: "Sube y baja" — Escala ascendente + descendente. */
function genScaleUpDown(): ExerciseState {
  const root = rand(NOTAS_POOL);
  const scale = buildScalePattern(root, "major", 15);

  const seq = buildScaleHandFriendlySequence(root, "major", {
    octaves: 1,
    direction: "both",
    maxFretSpan: 4,
    startString: 6,
    startFret: Math.floor(Math.random() * 3),
  });

  return {
    exerciseId: "scale-updown",
    title: `Sube y baja en ${noteToSpanish(root)} Mayor`,
    instructions: [
      "Primero sube la escala nota por nota.",
      "Cuando llegues arriba, ¡baja por el mismo camino!",
      "Esto se llama 'subir y bajar la escalera'.",
    ],
    neckPositions: scale.positions,
    sequence: seq,
    activeNote: seq.length > 0 ? { string: seq[0].string, fret: seq[0].fret } : null,
    stars: 3,
    tips: ["Subir y bajar fortalece la memoria muscular de tus dedos."],
    progressTotal: seq.length,
  };
}

/** EJERCICIO: "Escala Pentatónica" — 5 notas, animada. */
function genPentatonic(): ExerciseState {
  const root = rand(NOTAS_POOL);
  const scale = buildScalePattern(root, "pentatonic_minor", 15);

  const seq = buildScaleHandFriendlySequence(root, "pentatonic_minor", {
    octaves: 1,
    direction: "both",
    maxFretSpan: 3,
    startString: 6,
    startFret: Math.floor(Math.random() * 4),
  });

  return {
    exerciseId: "pentatonic",
    title: `La escala Pentatónica de ${noteToSpanish(root)}`,
    instructions: [
      "La pentatónica solo tiene 5 notas.",
      "Es más fácil porque tiene menos notas que la escala completa.",
      "Muchos solos de rock usan esta escala.",
    ],
    neckPositions: scale.positions,
    sequence: seq,
    activeNote: seq.length > 0 ? { string: seq[0].string, fret: seq[0].fret } : null,
    stars: 3,
    tips: [
      "Penta = 5. Tónica + tercera menor + cuarta + quinta + séptima menor.",
      "Con solo 5 notas ya puedes improvisar.",
    ],
    progressTotal: seq.length,
  };
}

// ============================================
// 4. TÉCNICA (los clásicos, mejorados)
// ============================================

function genWarmup1234(): ExerciseState {
  const positions: NeckPosition[] = [];
  const seq: NeckPosition[] = [];
  for (let s = 6; s >= 1; s--) {
    for (let f = 1; f <= 4; f++) {
      const pos = getNoteAtPosition(s, f);
      positions.push({
        string: s,
        fret: f,
        note: pos.note,
        highlight: "scale",
        finger: f,
      });
      seq.push({
        string: s,
        fret: f,
        note: pos.note,
        highlight: "active",
        finger: f,
      });
    }
  }

  return {
    exerciseId: "warmup-1234",
    title: "Calentamiento 1-2-3-4",
    instructions: [
      "Coloca un dedo en cada traste: 1, 2, 3, 4.",
      "Toca cada nota con el dedo que indica el número.",
      "Baja cuerda por cuerda hasta llegar a la 1.",
    ],
    neckPositions: positions,
    sequence: seq,
    activeNote: { string: 6, fret: 1 },
    stars: 2,
    tips: ["Este ejercicio calienta todos los dedos por igual."],
    progressTotal: seq.length,
  };
}

function genSpider(): ExerciseState {
  const positions: NeckPosition[] = [];
  const seq: NeckPosition[] = [];
  for (let s = 6; s >= 1; s--) {
    const pattern = [1, 3, 2, 4];
    for (const f of pattern) {
      const pos = getNoteAtPosition(s, f);
      const p: NeckPosition = {
        string: s,
        fret: f,
        note: pos.note,
        highlight: "active",
        finger: f,
      };
      positions.push({ ...p, highlight: "scale" });
      seq.push(p);
    }
  }

  return {
    exerciseId: "spider",
    title: "Ejercicio Araña",
    instructions: [
      "La araña camina así: 1-3-2-4.",
      "Nota cómo los dedos se cruzan.",
      "Esto ayuda a que cada dedo sea independiente.",
    ],
    neckPositions: positions,
    sequence: seq,
    activeNote: { string: 6, fret: 1 },
    stars: 3,
    tips: ["Ve despacio al principio. La velocidad viene sola con la práctica."],
    progressTotal: seq.length,
  };
}

function genHammerOns(): ExerciseState {
  const positions: NeckPosition[] = [];
  const seq: NeckPosition[] = [];
  for (let s = 6; s >= 1; s--) {
    const p1: NeckPosition = {
      string: s,
      fret: 7,
      note: getNoteAtPosition(s, 7).note,
      highlight: "scale",
      finger: 1,
    };
    const p2: NeckPosition = {
      string: s,
      fret: 8,
      note: getNoteAtPosition(s, 8).note,
      highlight: "active",
      finger: 2,
    };
    positions.push(p1, { ...p2, highlight: "scale" });
    seq.push(p1, p2);
  }

  return {
    exerciseId: "hammer-ons",
    title: "Hammer-Ons 7 → 8",
    instructions: [
      "Toca el traste 7 con el dedo 1.",
      "Sin volver a tocar la cuerda, 'martilla' el traste 8 con el dedo 2.",
      "¡Escucha cómo suenan las dos notas con un solo rasgueo!",
    ],
    neckPositions: positions,
    sequence: seq,
    activeNote: { string: 6, fret: 7 },
    stars: 2,
    tips: ["El truco está en el dedo 2: golpea fuerte y rápido la cuerda."],
    progressTotal: seq.length,
  };
}

function genStringSkipping(): ExerciseState {
  const positions: NeckPosition[] = [];
  const seq: NeckPosition[] = [];
  const strings = [6, 4, 5, 3, 4, 2, 3, 1];
  for (const s of strings) {
    const f = s % 2 === 0 ? 5 : 7;
    const p: NeckPosition = {
      string: s,
      fret: f,
      note: getNoteAtPosition(s, f).note,
      highlight: "active",
      finger: 1,
    };
    positions.push({ ...p, highlight: "scale" });
    seq.push(p);
  }

  return {
    exerciseId: "string-skipping",
    title: "Saltos de Cuerda",
    instructions: [
      "Toca una cuerda, luego salta a otra no vecina.",
      "6 → 4 → 5 → 3 → 4 → 2 → 3 → 1",
      "¡Tu mano derecha debe saltar sin tocar las cuerdas del medio!",
    ],
    neckPositions: positions,
    sequence: seq,
    activeNote: { string: 6, fret: 5 },
    stars: 3,
    tips: ["Controla la mano derecha: apoya la muñeca y salta con precisión."],
    progressTotal: seq.length,
  };
}

// ============================================
// Catálogo de Ejercicios
// ============================================

export const EXERCISES: ExerciseDef[] = [
  // ─── NOTAS ───
  {
    id: "what-note",
    title: "¿Qué nota es esta?",
    category: "notas",
    difficulty: "facil",
    type: "quiz",
    description: "Mira una nota en el mástil y di cómo se llama.",
    emoji: "🎯",
    bpm: 0,
    generate: genWhatNoteIsThis,
    checkAnswer: (state, answer) => {
      const correct = answer === state.correctAnswer;
      return {
        correct,
        message: correct
          ? "¡Exacto! Esa es la nota correcta."
          : `Casi... la nota correcta era "${state.correctAnswer}". ¡Inténtalo de nuevo!`,
        emoji: correct ? "🌟" : "💪",
        stars: correct ? 3 : 0,
      };
    },
  },
  {
    id: "find-note",
    title: "Encuentra la nota",
    category: "notas",
    difficulty: "facil",
    type: "click",
    description: "Busca una nota específica en cualquier lugar del mástil.",
    emoji: "🔍",
    bpm: 0,
    generate: genFindTheNote,
    checkAnswer: (state, answer: { string: number; fret: number }) => {
      const targets = state.targetPositions || [];
      const correct = targets.some((t) => t.string === answer.string && t.fret === answer.fret);
      return {
        correct,
        message: correct
          ? `¡Bien! Ese sí es un ${state.targetNote}.`
          : "Esa no es la nota que buscamos. Mira los nombres de las cuerdas como pista.",
        emoji: correct ? "🎉" : "🤔",
        stars: correct ? 3 : 0,
      };
    },
  },
  {
    id: "follow-drawing",
    title: "Sigue el dibujito",
    category: "notas",
    difficulty: "medio",
    type: "sequence",
    description: "Aprende el truco del profe para encontrar la misma nota en todas las cuerdas.",
    emoji: "🗺️",
    bpm: 50,
    generate: genFollowTheDrawing,
  },
  {
    id: "grave-to-agudo",
    title: "De grave a agudo",
    category: "notas",
    difficulty: "medio",
    type: "sequence",
    description: "Toca todas las posiciones de una nota, de la cuerda gruesa a la delgada.",
    emoji: "⬇️",
    bpm: 60,
    generate: genPlayAllGraveToAgudo,
  },

  // ─── ACORDES ───
  {
    id: "chord-notes",
    title: "Las 3 notas del acorde",
    category: "acordes",
    difficulty: "facil",
    type: "quiz",
    description: "Mira un acorde Mayor en el mástil y di qué 3 notas lo forman.",
    emoji: "🎹",
    bpm: 0,
    generate: genChordNotesQuiz,
    checkAnswer: (state, answer) => {
      const correct = answer === state.correctAnswer;
      return {
        correct,
        message: correct
          ? "¡Perfecto! Esas son las 3 notas del acorde Mayor."
          : `No exactamente... Las notas correctas son: ${state.correctAnswer}.`,
        emoji: correct ? "🏆" : "📚",
        stars: correct ? 3 : 1,
      };
    },
  },
  {
    id: "find-bass",
    title: "¿Qué nota está en el bajo?",
    category: "acordes",
    difficulty: "medio",
    type: "quiz",
    description: "Identifica la nota más grave del acorde según su inversión.",
    emoji: "🎸",
    bpm: 0,
    generate: genFindTheBass,
    checkAnswer: (state, answer) => {
      const correct = answer === state.correctAnswer;
      return {
        correct,
        message: correct
          ? "¡Excelente oído! Esa es la nota del bajo."
          : `El bajo era "${state.correctAnswer}". Recuerda: en ${state.title.includes("fundamental") ? "posición fundamental el bajo es la Raíz" : state.title.includes("1ª") ? "1ª inversión el bajo es la Tercera" : "2ª inversión el bajo es la Quinta"}.`,
        emoji: correct ? "🎵" : "🎼",
        stars: correct ? 3 : 1,
      };
    },
  },
  {
    id: "build-chord",
    title: "Construye el acorde",
    category: "acordes",
    difficulty: "dificil",
    type: "click",
    description: "Encuentra las 3 notas de un acorde Mayor en cualquier parte del mástil.",
    emoji: "🏗️",
    bpm: 0,
    generate: genBuildTheChord,
    checkAnswer: (state, answer: { string: number; fret: number }) => {
      const targets = state.targetPositions || [];
      const correct = targets.some((t) => t.string === answer.string && t.fret === answer.fret);
      // Verificar que sea una de las 3 notas correctas del acorde
      const pos = getNoteAtPosition(answer.string, answer.fret);
      const noteName = noteToSpanish(pos.note);
      const needed = (state.correctAnswer as string[] || []);
      const isNeededNote = needed.includes(noteName);

      if (correct && isNeededNote) {
        return {
          correct: true,
          message: `¡Sí! Ese ${noteName} es parte del acorde.`,
          emoji: "🧱",
          stars: 3,
        };
      }
      return {
        correct: false,
        message: "Esa nota no es la que buscamos. Revisa las pistas del panel derecho.",
        emoji: "🔧",
        stars: 0,
      };
    },
  },

  // ─── ESCALAS ───
  {
    id: "scale-climb",
    title: "Sube la escala",
    category: "escalas",
    difficulty: "facil",
    type: "sequence",
    description: "Toca una escala Mayor nota por nota, de grave a agudo.",
    emoji: "🪜",
    bpm: 60,
    generate: genScaleClimb,
  },
  {
    id: "scale-updown",
    title: "Sube y baja",
    category: "escalas",
    difficulty: "medio",
    type: "sequence",
    description: "Sube la escala y vuelve por el mismo camino.",
    emoji: "↕️",
    bpm: 55,
    generate: genScaleUpDown,
  },
  {
    id: "pentatonic",
    title: "Escala Pentatónica",
    category: "escalas",
    difficulty: "medio",
    type: "sequence",
    description: "Practica la escala de 5 notas que usan los guitarristas de rock.",
    emoji: "🎸",
    bpm: 65,
    generate: genPentatonic,
  },

  // ─── TÉCNICA ───
  {
    id: "warmup-1234",
    title: "Calentamiento 1-2-3-4",
    category: "tecnica",
    difficulty: "facil",
    type: "practice",
    description: "Independencia de dedos: toca 4 trastes con 4 dedos en cada cuerda.",
    emoji: "🔥",
    bpm: 60,
    generate: genWarmup1234,
  },
  {
    id: "spider",
    title: "Ejercicio Araña",
    category: "tecnica",
    difficulty: "medio",
    type: "practice",
    description: "Patrón cruzado 1-3-2-4 para separar bien los dedos.",
    emoji: "🕷️",
    bpm: 50,
    generate: genSpider,
  },
  {
    id: "hammer-ons",
    title: "Hammer-Ons",
    category: "tecnica",
    difficulty: "facil",
    type: "practice",
    description: "Aprende a martillar el dedo para sonar dos notas con un solo rasgueo.",
    emoji: "🔨",
    bpm: 70,
    generate: genHammerOns,
  },
  {
    id: "string-skipping",
    title: "Saltos de Cuerda",
    category: "tecnica",
    difficulty: "dificil",
    type: "practice",
    description: "Salta entre cuerdas no vecinas para controlar la mano derecha.",
    emoji: "🦘",
    bpm: 60,
    generate: genStringSkipping,
  },
];

// ─── Helpers de acceso ───

export function getExercisesByCategory(cat: ExerciseCategory): ExerciseDef[] {
  return EXERCISES.filter((e) => e.category === cat);
}

export function getExercisesByDifficulty(diff: ExerciseDifficulty): ExerciseDef[] {
  return EXERCISES.filter((e) => e.difficulty === diff);
}

export function getExercise(id: string): ExerciseDef | undefined {
  return EXERCISES.find((e) => e.id === id);
}

export const CATEGORIES: { id: ExerciseCategory; label: string; emoji: string }[] = [
  { id: "notas", label: "Notas", emoji: "🎵" },
  { id: "acordes", label: "Acordes", emoji: "🎹" },
  { id: "escalas", label: "Escalas", emoji: "🪜" },
  { id: "tecnica", label: "Técnica", emoji: "🔥" },
];

export const DIFFICULTY_LABELS: Record<ExerciseDifficulty, string> = {
  facil: "Fácil",
  medio: "Medio",
  dificil: "Difícil",
};

export const DIFFICULTY_COLORS: Record<ExerciseDifficulty, string> = {
  facil: "#6B8E5B",
  medio: "#D4BA7A",
  dificil: "#7B2335",
};

/** Generadores con tónica opcional (camino principiante / sesiones guiadas). */
export function generateWhatNoteState(preferredNote?: NoteName): ExerciseState {
  return genWhatNoteIsThis(preferredNote);
}
export function generateFindNoteState(preferredNote?: NoteName): ExerciseState {
  return genFindTheNote(preferredNote);
}
export function generateScaleClimbState(preferredRoot?: NoteName): ExerciseState {
  return genScaleClimb(preferredRoot);
}
